/**
 * Spec 016: "Pyydä tarjous" in the cart.
 *
 * Sends the current cart to /apps/bc/cart-to-quote, which creates a BC sales
 * quote for the logged-in customer. On success the cart is emptied and the
 * customer lands on the new quote; on any failure the cart is left exactly as
 * it was, so nothing is lost and the request can simply be retried.
 *
 * The endpoint is idempotent per cart token: a double click or a retry
 * answers with the quote already created rather than creating another.
 */
document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-cart-quote-request]');
  if (!root) {
    return;
  }
  const toggle = root.querySelector('[data-cart-quote-request-toggle]');
  const form = root.querySelector('[data-cart-quote-request-form]');
  const submit = root.querySelector('[data-cart-quote-request-submit]');
  const cancel = root.querySelector('[data-cart-quote-request-cancel]');
  const errorBox = root.querySelector('[data-cart-quote-request-error]');
  const spinner = submit.querySelector('.loading-overlay__spinner');
  const quotesUrl = form.dataset.quotesUrl;
  let messages = {};
  try {
    messages = JSON.parse(form.dataset.messages);
  } catch (e) {
    console.error('cart-to-quote: unreadable messages', e);
  }

  const IN_PROGRESS_POLLS = 5;
  const IN_PROGRESS_DELAY_MS = 2000;
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const setOpen = (open) => {
    form.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      form.querySelector('input, textarea').focus();
    }
  };

  const setBusy = (busy) => {
    submit.disabled = busy;
    submit.setAttribute('aria-disabled', String(busy));
    submit.classList.toggle('loading', busy);
    spinner.classList.toggle('hidden', !busy);
  };

  const showError = (text, items) => {
    errorBox.textContent = text || messages.generic || '';
    if (items && items.length) {
      const list = document.createElement('ul');
      items.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item.title || item.sku;
        list.appendChild(li);
      });
      errorBox.appendChild(list);
    }
    errorBox.hidden = false;
  };

  const clearError = () => {
    errorBox.textContent = '';
    errorBox.hidden = true;
  };

  const errorFor = (status, body) => {
    switch (body && body.outcome) {
      case 'no-bc-customer':
        return [messages.noCustomer];
      case 'unknown-items':
        return [messages.unknownItems, body.items];
      case 'invalid-attributes':
        return [messages.invalid];
      case 'empty-cart':
        return [messages.empty];
      case 'in-progress':
        return [messages.inProgress];
      default:
        return [messages.generic];
    }
  };

  const postQuoteRequest = (payload) =>
    fetch(window.Shopify.routes.root + 'apps/bc/cart-to-quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((body) => ({ status: response.status, body: body }))
    );

  toggle.addEventListener('click', () => {
    setOpen(form.hidden);
  });
  cancel.addEventListener('click', () => {
    clearError();
    setOpen(false);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearError();
    setBusy(true);

    try {
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { 'Content-Type': 'application/json' },
      });
      const cart = await cartResponse.json();
      if (!cart.items || cart.items.length === 0) {
        showError(messages.empty);
        return;
      }

      const formData = new FormData(form);
      const payload = {
        ...cart,
        cartToken: cart.token,
        attributes: {
          reference: formData.get('reference'),
          message: formData.get('message'),
        },
      };

      let result = await postQuoteRequest(payload);
      // Another submission of this same cart is still being processed (a
      // double click, or a retry after a slow answer): wait for it to finish.
      for (let poll = 0; result.status === 409 && IN_PROGRESS_POLLS > poll; poll++) {
        await sleep(IN_PROGRESS_DELAY_MS);
        result = await postQuoteRequest(payload);
      }

      const outcome = result.body && result.body.outcome;
      if (result.status === 200 && (outcome === 'created' || outcome === 'already-created')) {
        // Only now is it safe to empty the cart: the quote exists in BC.
        await fetch(window.Shopify.routes.root + 'cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).catch((e) => console.error('cart-to-quote: could not clear cart', e));
        window.location =
          quotesUrl +
          '?quoteId=' +
          encodeURIComponent(result.body.quoteNumber) +
          '&requested=1';
        return;
      }

      const [text, items] = errorFor(result.status, result.body);
      showError(text, items);
    } catch (e) {
      console.error('cart-to-quote: request failed', e);
      showError(messages.generic);
    } finally {
      setBusy(false);
    }
  });
});
